import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/AuthContext";
import {
  PackageSearch, Truck, CheckCircle2, AlertCircle, Boxes, ShoppingCart,
  PackagePlus, RotateCcw, Hourglass, FileBarChart,
} from "lucide-react";

const KPI = ({ icon: Icon, label, value, tone = "slate", to, testId }) => (
  <Link to={to || "#"} className="block" data-testid={testId}>
    <Card className="hover:shadow-md transition">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-md bg-${tone}-100 text-${tone}-700`}><Icon size={20} /></div>
        <div className="min-w-0">
          <div className="text-xs text-slate-600 truncate">{label}</div>
          <div className="font-heading text-2xl font-bold text-slate-900 leading-none">{value ?? "—"}</div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

export default function SparesDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/spares/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  if (err) return (
    <div className="max-w-3xl" data-testid="spares-dashboard-error">
      <Card><CardContent className="p-6">
        <div className="text-rose-700 font-semibold">Could not load Spares Dashboard</div>
        <div className="text-xs text-slate-600 mt-1">{String(err)}</div>
      </CardContent></Card>
    </div>
  );

  const k = data?.kpis || {};
  const lowStock = data?.low_stock_preview || [];

  return (
    <div className="space-y-5" data-testid="spares-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Spares Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">{user?.name} · Spares department snapshot.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <KPI icon={PackageSearch}  label="Pending Approval"          value={k.parts_requests_pending_approval} tone="rose"     to="/parts-requests"             testId="kpi-pending-approval" />
        <KPI icon={CheckCircle2}   label="Approved · Pending Dispatch" value={k.approved_pending_dispatch}     tone="amber"    to="/parts-requests"             testId="kpi-approved-pending" />
        <KPI icon={Truck}          label="Dispatched Today"          value={k.dispatched_today}              tone="indigo"   to="/spares/pods"                testId="kpi-dispatched-today" />
        <KPI icon={Truck}          label="Parts in Transit"          value={k.parts_in_transit}              tone="sky"      to="/parts-requests"             testId="kpi-in-transit" />
        <KPI icon={CheckCircle2}   label="Delivered to Technician"   value={k.delivered_to_technician}       tone="emerald"  to="/parts-requests"             testId="kpi-delivered" />
        <KPI icon={AlertCircle}    label="PNA Cases"                 value={k.pna_cases}                     tone="rose"     to="/parts-requests"             testId="kpi-pna" />
        <KPI icon={Boxes}          label="Low Stock Items"           value={k.low_stock_items}               tone="amber"    to="/inventory"                  testId="kpi-low-stock" />
        <KPI icon={PackagePlus}    label="Taken · Fresh Units"       value={k.takeouts_fresh}                tone="emerald"  to="/spares/part-takeout"        testId="kpi-takeout-fresh" />
        <KPI icon={PackagePlus}    label="Taken · Damaged/Refurb."   value={k.takeouts_damaged_refurbished}  tone="indigo"   to="/spares/part-takeout"        testId="kpi-takeout-dmg" />
        <KPI icon={ShoppingCart}   label="Market Purchase Pending"   value={k.market_purchase_pending}       tone="amber"    to="/parts-requests"             testId="kpi-market-pending" />
        <KPI icon={RotateCcw}      label="Tech Parts Pending Return" value={k.technician_parts_pending_return} tone="rose"   to="/spares/pods"                testId="kpi-pending-return" />
      </div>

      {lowStock.length > 0 && (
        <Card data-testid="low-stock-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-semibold text-slate-900">Low Stock (≤2 units)</div>
              <Link to="/inventory" className="text-xs text-slate-600 hover:underline">Open Inventory →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
              {lowStock.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-1">
                  <div className="text-slate-800 truncate">
                    {p.part_name}
                    {p.part_code && <span className="text-slate-500 font-mono ml-1 text-xs">({p.part_code})</span>}
                  </div>
                  <div className="text-rose-700 font-mono">{p.total} left</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
        <Link to="/parts-requests" data-testid="link-parts-requests">
          <Card><CardContent className="p-3 flex items-center gap-2"><PackageSearch size={16} className="text-slate-700" /><span className="font-semibold text-slate-900">Parts Requests</span></CardContent></Card>
        </Link>
        <Link to="/spares/stock-entry" data-testid="link-stock-entry">
          <Card><CardContent className="p-3 flex items-center gap-2"><PackagePlus size={16} className="text-slate-700" /><span className="font-semibold text-slate-900">Add Stock</span></CardContent></Card>
        </Link>
        <Link to="/spares/part-takeout" data-testid="link-part-takeout">
          <Card><CardContent className="p-3 flex items-center gap-2"><Hourglass size={16} className="text-slate-700" /><span className="font-semibold text-slate-900">Part Takeout</span></CardContent></Card>
        </Link>
        <Link to="/spares/part-approval-report" data-testid="link-part-approval-report">
          <Card><CardContent className="p-3 flex items-center gap-2"><FileBarChart size={16} className="text-slate-700" /><span className="font-semibold text-slate-900">Approval Report</span></CardContent></Card>
        </Link>
      </div>
    </div>
  );
}
